/**
 * The `race_timeline` project object written by server_python/scripts/gp50/0_race_fleet.py:
 * per race, every boat on one shared 1 s grid, with course progress and distance to leader
 * measured along the course, plus the course geometry the stats used.
 */
import { apiEndpoints } from "@config/env";
import type { RaceDrivers } from "./raceDrivers";
import { getData } from "../../../utils/global";
import { error as logError } from "../../../utils/console";

export type LatLon = [number, number];

export interface RaceTimelineBoat {
  lat: (number | null)[];
  lon: (number | null)[];
  progress_m: (number | null)[];
  /** True distance behind the leader of that second, along the course (the DTL chart). */
  dtl_m: (number | null)[];
  /** Like-for-like metres lost on the leader at the same place on the course (race_fleet "LIKE FOR
   *  LIKE"); its change over a stretch is what the boat lost there. Absent on older timelines. */
  lfl_m?: (number | null)[];
  /** Seconds behind the first boat to reach the same place (absent on older timelines). */
  behind_s?: (number | null)[];
  leg: (number | null)[];
  bsp_kts: (number | null)[];
  tws_kts: (number | null)[];
  twd_deg: (number | null)[];
  vmc_ratio: (number | null)[];
  /** T / G / R / B on the second nearest each maneuver instant (map letters), else null. */
  mnvr: (string | null)[];
  /** Metres from the bottom->top course line, left looking upwind > 0 (absent on older timelines). */
  lateral_m?: (number | null)[];
  /** Lateral offset minus the leader's at that second (left of the leader > 0). */
  leverage_m?: (number | null)[];
  first_index: number | null;
  /** Seconds from `t0` at which the boat crossed the finish; null when it did not (absent on older timelines). */
  finish_offset_s?: number | null;
}

export interface RaceTimelineMark {
  name: string;
  position: string;
  lat: number;
  lon: number;
}

export interface RaceTimelineCourse {
  axis_deg: number | null;
  top: LatLon | null;
  bottom: LatLon | null;
  bottom_kind: string | null;
  start: LatLon | null;
  finish: LatLon | null;
  marks: RaceTimelineMark[];
  legs: {
    leg: number;
    axis_deg: number;
    length_m: number | null;
    source: string;
    /** reaching / upwind / downwind. Absent on timelines written before the leg-kind fix. */
    kind?: "reaching" | "upwind" | "downwind";
  }[];
  /** Reaching mark. Present when the course opened with a reach to M1. */
  m1?: LatLon | null;
}

export interface RaceTimelineRace {
  race: number;
  t0: number | null;
  dt: number;
  n: number;
  gun_offset_s: number | null;
  /** When the LEG LEADER completed each leg, seconds from t0. */
  legs: { leg: number; leader_end_offset_s: number | null }[];
  course: RaceTimelineCourse;
  /** The marks as laid at each change during the race, seconds from t0 (race_fleet.course_changes):
   *  the start line is often re-laid as the leeward gate after the gun. Absent when nothing moved
   *  and on timelines written before 2026-10-13; then `course.marks` holds for the whole race. */
  course_changes?: { t: number; marks: RaceTimelineMark[] }[];
  boats: Record<string, RaceTimelineBoat>;
  /** The race's wind, gun to last finish (0_race_fleet.py race_wind): the boats' own sensors, else the
   *  race marks'. Absent on timelines written before 2026-10-09. */
  wind?: RaceTimelineWind | null;
  /** Where each finish gap was made (race_fleet.race_drivers); absent before 2026-10-09. */
  drivers?: RaceDrivers | null;
}

export interface RaceTimelineWind {
  source: "boats" | "marks" | null;
  tws_avg_kts?: number | null;
  /** 5th / 95th percentile. */
  tws_min_kts?: number | null;
  tws_max_kts?: number | null;
  twd_avg_deg?: number | null;
  /** 5th-95th percentile spread of direction. */
  twd_range_deg?: number | null;
  marks?: { name: string; tws_avg_kts: number | null; twd_avg_deg: number | null }[];
}

const KPH_PER_KT = 1.852;

/** The wind of one or several races (the "All" selection) in km/h, the unit race_stats uses, or null
 *  when none of them carries a wind record. Several races: mean of the means, the lowest low, the
 *  highest high, the circular mean direction and the mean spread. */
export function timelineWindKph(races: (RaceTimelineRace | null | undefined)[]): {
  tws: number | null; twsMin: number | null; twsMax: number | null; twd: number | null; shift: number | null;
  source: "boats" | "marks";
} | null {
  const winds = races.map((r) => r?.wind).filter((w): w is RaceTimelineWind => !!w && !!w.source && w.tws_avg_kts != null);
  if (winds.length === 0) {return null;}
  const vals = (k: keyof RaceTimelineWind) =>
    winds.map((w) => w[k]).filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  const mean = (vs: number[]) => (vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null);
  const kph = (v: number | null) => (v == null ? null : v * KPH_PER_KT);
  const twd = vals("twd_avg_deg");
  const twdMean = twd.length
    ? ((Math.atan2(
        twd.reduce((a, v) => a + Math.sin((v * Math.PI) / 180), 0),
        twd.reduce((a, v) => a + Math.cos((v * Math.PI) / 180), 0),
      ) * 180) / Math.PI + 360) % 360
    : null;
  const lo = vals("tws_min_kts");
  const hi = vals("tws_max_kts");
  return {
    tws: kph(mean(vals("tws_avg_kts"))),
    twsMin: kph(lo.length ? Math.min(...lo) : null),
    twsMax: kph(hi.length ? Math.max(...hi) : null),
    twd: twdMean,
    shift: mean(vals("twd_range_deg")),
    source: winds.every((w) => w.source === "boats") ? "boats" : "marks",
  };
}

/** All races of a day keyed by race number (string, as the race dropdown uses). */
export async function fetchRaceTimeline(
  className: string,
  projectId: string | number,
  date: string,
  signal?: AbortSignal
): Promise<Record<string, RaceTimelineRace>> {
  const url = `${apiEndpoints.app.projects}/object?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(String(projectId))}&date=${encodeURIComponent(date)}&object_name=race_timeline`;
  try {
    const res = await getData(url, signal);
    if (!res?.success || res.data == null) {return {};}
    // The app server returns the stored JSON as `data`; older envelopes wrapped it in `value`.
    let value: unknown = res.data;
    if (value && typeof value === "object" && "value" in (value as Record<string, unknown>)) {
      value = (value as Record<string, unknown>).value;
    }
    if (typeof value === "string") {value = JSON.parse(value);}
    const races = (value as { races?: RaceTimelineRace[] } | null)?.races;
    const out: Record<string, RaceTimelineRace> = {};
    for (const r of Array.isArray(races) ? races : []) {
      if (r && r.boats && r.n > 0) {out[String(r.race)] = r;}
    }
    return out;
  } catch (err) {
    logError("RaceSummary: fetch race_timeline failed", err);
    return {};
  }
}

/** One markwind / marks timestep for the race-summary course view (same source as the fleet map). */
export interface MarkwindCourseSnapshot {
  /** Unix ms of the snapshot DATETIME. */
  ms: number;
  marks: RaceTimelineMark[];
}

function parseProjectObjectValue(raw: unknown): unknown {
  let value: unknown = raw;
  if (value && typeof value === "object" && "value" in (value as Record<string, unknown>)) {
    value = (value as Record<string, unknown>).value;
  }
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

/** GP50 markwind names → course roles (matches race_course_axis._role). */
function markwindRole(name: string, position?: string): string {
  const pos = (position || "").toUpperCase().trim();
  if (pos && pos !== "MARK") return pos;
  const n = (name || "").toUpperCase().trim();
  if (n.startsWith("WG") || n.startsWith("TOP") || n.includes("WINDWARD")) return "WINDWARD";
  if (n.startsWith("LG") || n.startsWith("BOTTOM") || n.startsWith("BTM") || n.includes("LEEWARD")) return "LEEWARD";
  if (n.startsWith("SL") || n.startsWith("START")) return "START";
  if (n.startsWith("FL") || n.startsWith("FINISH")) return "FINISH";
  if (n === "M1" || n === "MK1" || n.startsWith("M1")) return "MARK";
  return pos || "MARK";
}

function marksFromTimestep(entry: Record<string, unknown>): RaceTimelineMark[] {
  const raw = (entry.MARKS ?? entry.marks) as unknown;
  if (!Array.isArray(raw)) return [];
  const out: RaceTimelineMark[] = [];
  for (const m of raw) {
    if (!m || typeof m !== "object") continue;
    const row = m as Record<string, unknown>;
    const name = String(row.NAME ?? row.name ?? "").trim();
    const lat = Number(row.LAT ?? row.lat);
    const lon = Number(row.LON ?? row.lon ?? row.lng);
    if (!name || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    out.push({
      name,
      position: markwindRole(name, String(row.POSITION ?? row.position ?? "")),
      lat,
      lon,
    });
  }
  return out;
}

/**
 * Day markwind (preferred) or marks object, as time-ordered course snapshots — the same series the
 * fleet map RaceCourseLayer animates. Race summary scrubbing uses these so marks move with the cursor.
 */
export async function fetchDayMarkCourse(
  className: string,
  projectId: string | number,
  date: string,
  signal?: AbortSignal
): Promise<MarkwindCourseSnapshot[]> {
  const dateParam = date.includes("-")
    ? date
    : date.length === 8
      ? `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
      : date;
  for (const objectName of ["markwind", "marks"] as const) {
    const url =
      `${apiEndpoints.app.projects}/object?class_name=${encodeURIComponent(className)}` +
      `&project_id=${encodeURIComponent(String(projectId))}` +
      `&date=${encodeURIComponent(dateParam)}&object_name=${objectName}`;
    try {
      const res = await getData(url, signal);
      if (!res?.success || res.data == null) continue;
      const value = parseProjectObjectValue(res.data);
      if (!Array.isArray(value) || value.length === 0) continue;
      const snaps: MarkwindCourseSnapshot[] = [];
      for (const entry of value) {
        if (!entry || typeof entry !== "object") continue;
        const row = entry as Record<string, unknown>;
        const dt = String(row.DATETIME ?? row.Datetime ?? row.datetime ?? "");
        const ms = Date.parse(dt);
        if (!Number.isFinite(ms)) continue;
        const marks = marksFromTimestep(row);
        if (marks.length === 0) continue;
        snaps.push({ ms, marks });
      }
      snaps.sort((a, b) => a.ms - b.ms);
      if (snaps.length > 0) return snaps;
    } catch (err) {
      logError(`RaceSummary: fetch ${objectName} failed`, err);
    }
  }
  return [];
}

/**
 * Markwind snapshots inside a race window as ``course_changes``-shaped entries (t seconds from t0).
 * Same nearest-at-or-before rule as RaceCourseLayer.findNearestByTime.
 */
export function markCourseChangesForRace(
  snaps: MarkwindCourseSnapshot[],
  t0UnixS: number | null | undefined,
  raceDurationS: number
): { t: number; marks: RaceTimelineMark[] }[] {
  if (!snaps.length || t0UnixS == null || !Number.isFinite(t0UnixS)) return [];
  const t0Ms = t0UnixS * 1000;
  const t1Ms = t0Ms + Math.max(raceDurationS, 1) * 1000;
  const inWindow = snaps.filter((s) => s.ms >= t0Ms - 60_000 && s.ms <= t1Ms + 30_000);
  const use = inWindow.length > 0 ? inWindow : snaps;
  return use.map((s) => ({
    t: (s.ms - t0Ms) / 1000,
    marks: s.marks,
  }));
}

/** Seconds from the gun for grid index i. */
export function raceSecondsAt(race: RaceTimelineRace, i: number): number {
  return i * (race.dt || 1) - (race.gun_offset_s ?? 0);
}

/** m:ss (or -m:ss before the gun). */
export function formatRaceClock(sec: number): string {
  const sign = sec < 0 ? "-" : "";
  const s = Math.round(Math.abs(sec));
  return `${sign}${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Rate of change of distance to leader, metres per `windowS` seconds, centred on each sample.
 * Negative = closing on the leader (gaining), positive = dropping back. null where undefined.
 */
export function dtlChange(dtl: (number | null)[], windowS = 10): (number | null)[] {
  const half = Math.max(1, Math.round(windowS / 2));
  return dtl.map((_, i) => {
    const a = dtl[Math.max(0, i - half)];
    const b = dtl[Math.min(dtl.length - 1, i + half)];
    return a == null || b == null ? null : b - a;
  });
}

/** Mark at the end of a leg. A reach to M1 is not the windward gate, and a reaching finish is not upwind. */
export function legEndLabel(leg: number, lastLeg: number, course?: RaceTimelineCourse | null): string {
  if (leg === lastLeg) {return "FINISH";}
  const kind = course?.legs?.find((l) => l.leg === leg)?.kind;
  if (kind === "reaching" || (kind == null && course?.m1 != null && leg === 1)) {
    return leg === 1 ? "M1" : "REACH";
  }
  if (kind === "downwind") {return "BOTTOM";}
  if (kind === "upwind") {return "TOP";}
  return leg % 2 === 1 ? "TOP" : "BOTTOM";
}
